package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * What a credential is allowed to do. Ten scopes, and the list is closed.
 *
 * Not six, not sixteen, not twenty-three — and applying to every credential there will ever be: a
 * personal token, a service token, an agent token, an MCP session.
 *
 * **The list was eight until store screenshots (October 2026), and it was opened once, on purpose.**
 * `screenshots:read` and `screenshots:write` were added because an agent retaking screenshots needs
 * to read the retake queue and upload images, and neither fits an existing scope without widening
 * it: `files:write` can obsolete keys, `strings:write` overwrites translations. Neither new scope
 * reaches a store, approves a capture, uploads a composed store image or creates a credential —
 * pushing stays a person's decision. Adding an eleventh needs the same written argument here, in
 * `docs/SPEC.md` and in `docs/AGENT-SURFACE-SPEC.md`; until then the list is closed again.
 *
 * **There is no `members:*`, no `billing:*`, no `providers:*` and no `tokens:*` scope, and there
 * never will be.** A token cannot touch people, money, provider keys, or other tokens — not by
 * policy, by *absence*. Those operations exist only under session auth, where a browser and a
 * password stand behind them.
 *
 * That single decision kills privilege escalation through a leaked credential, kills confused-deputy
 * token minting, and kills "an agent that can rotate a credential can exfiltrate one" outright, at
 * no implementation cost. It is the reason an agent fleet can be pointed at Lingvo unsupervised: the
 * blast radius is designed rather than hoped for.
 *
 * **Effective permission = intersection(token scopes, the owner's membership role right now, the
 * token's project allowlist), resolved on every call.** Never baked into the token at mint time — a
 * demotion narrows a credential immediately, and a removal kills it.
 */
@Serializable
enum class TokenScope(
    /** The wire spelling: what a person types on the command line and reads in a config file. */
    val id: String,
    val description: String,
) {
    STRINGS_READ("strings:read", "Read projects, keys and translations"),
    STRINGS_WRITE("strings:write", "Write translations and add keys"),
    STRINGS_APPROVE("strings:approve", "Mark translations approved"),
    GLOSSARY_READ("glossary:read", "Read glossary terms"),
    GLOSSARY_WRITE("glossary:write", "Change glossary terms"),
    FILES_WRITE("files:write", "Import and export files"),
    QA_RUN("qa:run", "Run QA checks and read readiness"),
    MT_SPEND("mt:spend", "Spend credits on machine translation"),
    SCREENSHOTS_READ("screenshots:read", "Read screenshot plans, coverage and captures"),
    SCREENSHOTS_WRITE("screenshots:write", "Upload screenshots and edit the shot plan"),
    ;

    /**
     * What granting this actually lets somebody do to you.
     *
     * The verb is not the consequence. "Import and export files" is what `files:write` *is*;
     * "can remove work" is what it *means*, and the second is the sentence somebody needs when they
     * are deciding whether to paste this credential into a CI config.
     */
    val consequence: String
        get() = when (this) {
            STRINGS_READ -> "Read every string in the projects it can reach, including source text."
            STRINGS_WRITE -> "Change translations. Existing text can be overwritten."
            STRINGS_APPROVE ->
                "Mark translations approved, which is what `pull --approved-only` and the release " +
                    "gate trust. Approval is normally a human's word."
            GLOSSARY_READ -> "Read the glossary and its required translations."
            GLOSSARY_WRITE -> "Change glossary terms, which changes what QA enforces everywhere."
            FILES_WRITE ->
                "Import files, and mark keys obsolete when they are missing from an import — this " +
                    "is the one that can remove work."
            QA_RUN -> "Run QA checks and read release readiness. Free and read-only."
            MT_SPEND ->
                "Spend machine-translation credits. A loop with this scope can spend a month's " +
                    "allowance in minutes."
            SCREENSHOTS_READ ->
                "See the shot plan and which captures are missing or stale, and download every " +
                    "screenshot, which can show screens of the app that are not released yet."
            SCREENSHOTS_WRITE ->
                "Upload, replace and delete screenshots and change the shot plan. A replaced image " +
                    "is kept only until the next replacement. It cannot put anything on a live " +
                    "store: pushing stays a person's decision."
        }

    /** The weakest role that may lend this out. A token can never do more than its owner. */
    val minimumRole: Role
        get() = when (this) {
            STRINGS_READ, GLOSSARY_READ, QA_RUN, SCREENSHOTS_READ -> Role.VIEWER
            STRINGS_WRITE, FILES_WRITE, GLOSSARY_WRITE, SCREENSHOTS_WRITE -> Role.TRANSLATOR
            STRINGS_APPROVE, MT_SPEND -> Role.ADMIN
        }

    companion object {
        fun byId(id: String): TokenScope? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }

        /**
         * What a new token gets when nobody chooses.
         *
         * Read-only, deliberately. The common first token is for a CI job that runs `lingvo qa`, and
         * a default that could write is a default that eventually does.
         */
        val DEFAULT: Set<TokenScope> = setOf(STRINGS_READ, QA_RUN, GLOSSARY_READ)

        /** Everything a `lingvo push`/`pull` loop needs, and nothing more. */
        val SYNC: Set<TokenScope> = setOf(STRINGS_READ, STRINGS_WRITE, FILES_WRITE, QA_RUN, GLOSSARY_READ)

        /**
         * An agent that retakes store screenshots: read the strings it photographs, read the queue,
         * upload. It cannot write strings, approve, push, or reach a composed store image.
         */
        val SCREENSHOT_AGENT: Set<TokenScope> = setOf(STRINGS_READ, SCREENSHOTS_READ, SCREENSHOTS_WRITE)
    }
}

/**
 * Whether a set of scopes can only narrow to another.
 *
 * The MCP session exchange rests on this: `lingvo mcp --read-only` hands in a full token and gets a
 * 60-minute session back. **That exchange can only ever narrow** — a downscope that could widen is
 * not a downscope, it is a privilege escalation with a friendly name.
 */
fun Set<TokenScope>.narrowsTo(requested: Set<TokenScope>): Boolean = containsAll(requested)

/**
 * One recorded use of a token, for the page that answers "what is this credential actually doing".
 *
 * `denied` is the interesting column. A token being refused repeatedly is either an agent looping on
 * a permission it will never get, or somebody probing — and both are invisible without a count.
 */
@Serializable
data class TokenUsageDto(
    val route: String,
    val calls: Int,
    val denied: Int,
    val lastUsedAt: String? = null,
)
